import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useShopContext } from './ShopContext';
import StripePayment from './StripePayment';
import StripeWrapper from './StripeWrapper';
import { validateEmail, validatePhone, validateZipCode } from '../../utils/validation';
import { formatPrice } from '../../utils/format';
import { apiRequest } from '../../utils/api';

// Custom hook for toast notifications
const useToast = () => {
  const [toast, setToast] = useState({ message: '', type: '', visible: false });

  const showToast = useCallback((message, type = 'info') => {
    setToast({ message, type, visible: true });
    setTimeout(() => {
      setToast({ message: '', type: '', visible: false });
    }, 3000);
  }, []);

  return { toast, showToast };
};

const Checkout = () => {
  const navigate = useNavigate();
  const shopContext = useShopContext();
  const { toast, showToast } = useToast();
  const [currentStep, setCurrentStep] = useState(0);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderId, setOrderId] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [formData, setFormData] = useState({
    shipping: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'LV'
    },
    payment: {
      paymentMethod: 'card',
    },
  });
  const [errors, setErrors] = useState({
    shipping: {},
    payment: {}
  });

  // Memoize validation functions
  const validateEmail = useCallback((email) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(email);
  }, []);

  const validatePhone = useCallback((phone) => {
    const re = /^[+]?[\d\s-]{8,}$/;
    return re.test(phone);
  }, []);

  const validateZipCode = useCallback((zipCode, country) => {
    if (country === 'LV') {
      return /^LV-\d{4}$/.test(zipCode) || /^\d{4}$/.test(zipCode);
    }
    return zipCode.length >= 4;
  }, []);

  // Memoize handlers
  const handleShippingChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      shipping: {
        ...prev.shipping,
        [name]: value
      }
    }));

    if (name === 'city' || name === 'country') {
      shopContext.updateShippingAddress({
        ...formData.shipping,
        [name]: value
      });
    }
  }, [formData.shipping, shopContext]);

  const handleShippingSubmit = useCallback((e) => {
    e.preventDefault();
    
    const newErrors = {};
    const required = ['firstName', 'lastName', 'email', 'phone', 'address', 'city', 'state', 'zipCode'];
    
    required.forEach(field => {
      if (!formData.shipping[field]) {
        newErrors[field] = 'Šis lauks ir obligāts';
      }
    });
    
    if (formData.shipping.email && !validateEmail(formData.shipping.email)) {
      newErrors.email = 'Lūdzu, ievadiet derīgu e-pasta adresi';
    }
    
    if (formData.shipping.phone && !validatePhone(formData.shipping.phone)) {
      newErrors.phone = 'Lūdzu, ievadiet derīgu tālruņa numuru';
    }

    if (!formData.shipping.zipCode ) {
      newErrors.zipCode = 'Lūdzu, ievadiet derīgu pasta indeksu';
    }
    
    setErrors(prev => ({
      ...prev,
      shipping: newErrors
    }));
    
    if (Object.keys(newErrors).length === 0) {
      shopContext.updateShippingAddress(formData.shipping);
      setCurrentStep(1);
      window.scrollTo(0, 0);
    }
  }, [formData.shipping, validateEmail, validatePhone, validateZipCode, shopContext]);

  const handlePaymentSuccess = async (paymentResult) => {
    try {
      console.log('Payment successful:', paymentResult);
      
      // Format order details
      const orderDetails = {
        ...paymentResult.orderDetails,
        billingDetails: {
          name: `${formData.shipping.firstName} ${formData.shipping.lastName}`,
          email: formData.shipping.email,
          phone: formData.shipping.phone,
          address: {
            line1: formData.shipping.address,
            city: formData.shipping.city,
            state: formData.shipping.state,
            postal_code: formData.shipping.zipCode,
            country: formData.shipping.country
          }
        },
        items: shopContext.cart.map(item => ({
          name: item.name,
          quantity: item.quantity,
          price: item.price
        })),
        total: shopContext.getFinalTotal()
      };

      // Send order confirmation email
      await apiRequest('/send-email', {
        method: 'POST',
        body: JSON.stringify({
          type: 'order',
          templateParams: {
            order_id: paymentResult.paymentIntent.id,
            name: `${formData.shipping.firstName} ${formData.shipping.lastName}`,
            email: formData.shipping.email,
            phone: formData.shipping.phone,
            address: `${formData.shipping.address}, ${formData.shipping.city}, ${formData.shipping.zipCode}, ${formData.shipping.country}`,
            service_description: shopContext.cart.map(item => `${item.name} - ${formatPrice(item.price)}`).join('\n'),
            total: formatPrice(shopContext.getFinalTotal()),
          },
        }),
      });

      // Clear the cart
      shopContext.clearCart();

      // Navigate to success page with order details
      navigate('/success', { 
        state: { orderDetails }
      });
    } catch (err) {
      console.error('Order confirmation error:', err);
      setErrors(prev => ({
        ...prev,
        payment: {
          ...prev.payment,
          error: err.message
        }
      }));
    }
  };

  const handlePaymentError = useCallback((error) => {
    console.error('Payment error:', error);
    showToast(error.message || 'Payment failed. Please try again.', 'error');
    setIsProcessing(false);
  }, [showToast]);

  // Memoize content components
  const shippingContent = useMemo(() => (
    <form onSubmit={handleShippingSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="col-span-1">
          <label className="block text-white mb-1">Vārds*</label>
          <input
            type="text"
            name="firstName"
            value={formData.shipping.firstName}
            onChange={handleShippingChange}
            className={`w-full bg-gray-800 text-white border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400 ${
              errors.shipping.firstName ? 'border-red-500' : 'border-gray-700'
            }`}
          />
          {errors.shipping.firstName && (
            <p className="text-red-500 text-sm mt-1">{errors.shipping.firstName}</p>
          )}
        </div>
        
        <div className="col-span-1">
          <label className="block text-white mb-1">Uzvārds*</label>
          <input
            type="text"
            name="lastName"
            value={formData.shipping.lastName}
            onChange={handleShippingChange}
            className={`w-full bg-gray-800 text-white border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400 ${
              errors.shipping.lastName ? 'border-red-500' : 'border-gray-700'
            }`}
          />
          {errors.shipping.lastName && (
            <p className="text-red-500 text-sm mt-1">{errors.shipping.lastName}</p>
          )}
        </div>
      </div>
      
      <div>
        <label className="block text-white mb-1">E-pasts*</label>
        <input
          type="email"
          name="email"
          value={formData.shipping.email}
          onChange={handleShippingChange}
          className={`w-full bg-gray-800 text-white border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400 ${
            errors.shipping.email ? 'border-red-500' : 'border-gray-700'
          }`}
        />
        {errors.shipping.email && (
          <p className="text-red-500 text-sm mt-1">{errors.shipping.email}</p>
        )}
      </div>
      
      <div>
        <label className="block text-white mb-1">Telefons*</label>
        <input
          type="tel"
          name="phone"
          value={formData.shipping.phone}
          onChange={handleShippingChange}
          className={`w-full bg-gray-800 text-white border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400 ${
            errors.shipping.phone ? 'border-red-500' : 'border-gray-700'
          }`}
        />
        {errors.shipping.phone && (
          <p className="text-red-500 text-sm mt-1">{errors.shipping.phone}</p>
        )}
      </div>
      
      <div>
        <label className="block text-white mb-1">Adrese*</label>
        <input
          type="text"
          name="address"
          value={formData.shipping.address}
          onChange={handleShippingChange}
          className={`w-full bg-gray-800 text-white border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400 ${
            errors.shipping.address ? 'border-red-500' : 'border-gray-700'
          }`}
        />
        {errors.shipping.address && (
          <p className="text-red-500 text-sm mt-1">{errors.shipping.address}</p>
        )}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="col-span-1">
          <label className="block text-white mb-1">Pilsēta*</label>
          <input
            type="text"
            name="city"
            value={formData.shipping.city}
            onChange={handleShippingChange}
            className={`w-full bg-gray-800 text-white border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400 ${
              errors.shipping.city ? 'border-red-500' : 'border-gray-700'
            }`}
          />
          {errors.shipping.city && (
            <p className="text-red-500 text-sm mt-1">{errors.shipping.city}</p>
          )}
        </div>
        
        <div className="col-span-1">
          <label className="block text-white mb-1">Novads*</label>
          <input
            type="text"
            name="state"
            value={formData.shipping.state}
            onChange={handleShippingChange}
            className={`w-full bg-gray-800 text-white border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400 ${
              errors.shipping.state ? 'border-red-500' : 'border-gray-700'
            }`}
          />
          {errors.shipping.state && (
            <p className="text-red-500 text-sm mt-1">{errors.shipping.state}</p>
          )}
        </div>
        
        <div className="col-span-1">
          <label className="block text-white mb-1">Pasta indekss*</label>
          <input
            type="text"
            name="zipCode"
            value={formData.shipping.zipCode}
            onChange={handleShippingChange}
            className={`w-full bg-gray-800 text-white border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400 ${
              errors.shipping.zipCode ? 'border-red-500' : 'border-gray-700'
            }`}
          />
          {errors.shipping.zipCode && (
            <p className="text-red-500 text-sm mt-1">{errors.shipping.zipCode}</p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-white mb-1">Valsts*</label>
        <select
          name="country"
          value={formData.shipping.country}
          onChange={handleShippingChange}
          className="w-full bg-gray-800 text-white border border-gray-700 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
        >
          <option value="LV">Latvija</option>
          <option value="EE">Igaunija</option>
          <option value="LT">Lietuva</option>
        </select>
      </div>
      
      <button 
        type="submit" 
        className="mt-6 w-full md:w-auto bg-green-400 hover:bg-green-500 text-black font-semibold py-3 px-6 rounded-md transition-colors"
      >
        Turpināt uz maksājumu
      </button>
    </form>
  ), [handleShippingSubmit, formData.shipping, errors.shipping]);

  const paymentContent = useMemo(() => (
    <div className="space-y-6">
      <div className="bg-gray-800 rounded-lg p-6">
        <h3 className="text-xl font-semibold text-white mb-4">Pasūtījuma kopsavilkums</h3>
        <div className="space-y-2">
          <div className="flex justify-between text-gray-300">
            <span>Preces kopā:</span>
            <span>€{shopContext.totalPrice.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-gray-300">
            <span>Piegāde:</span>
            <span>€{shopContext.deliveryCost.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-white font-semibold text-lg pt-2 border-t border-gray-700">
            <span>Kopā:</span>
            <span>€{shopContext.getFinalTotal().toFixed(2)}</span>
          </div>
        </div>
      </div>

      {isProcessing ? (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-400"></div>
          <span className="ml-3 text-white">Apstrādājam maksājumu...</span>
        </div>
      ) : (
        <>
          {shopContext.getFinalTotal() > 0 ? (
            <StripeWrapper amount={Math.round(shopContext.getFinalTotal() * 100)}>
              <StripePayment
                onSuccess={handlePaymentSuccess}
                onError={handlePaymentError}
                isProcessing={isProcessing}
                customerEmail={formData.shipping.email}
                amount={Math.round(shopContext.getFinalTotal() * 100)}
                billingDetails={{
                  firstName: formData.shipping.firstName,
                  lastName: formData.shipping.lastName,
                  email: formData.shipping.email,
                  phone: formData.shipping.phone,
                  address: formData.shipping.address,
                  city: formData.shipping.city,
                  state: formData.shipping.state,
                  zipCode: formData.shipping.zipCode,
                  country: formData.shipping.country
                }}
              />
            </StripeWrapper>
          ) : (
            <div className="text-red-500 text-center py-4">
              Nevar apstrādāt maksājumu - summa ir 0
            </div>
          )}
        </>
      )}

      <button 
        type="button" 
        onClick={() => {
          setCurrentStep(0);
          window.scrollTo(0, 0);
        }} 
        className="mt-4 text-gray-300 border border-gray-600 hover:text-white hover:border-gray-400 font-medium py-2 px-4 rounded-md"
        disabled={isProcessing}
      >
        Atpakaļ
      </button>
    </div>
  ), [shopContext, isProcessing, formData.shipping, handlePaymentSuccess, handlePaymentError]);

  // Display warning if cart is empty
  useEffect(() => {
    if (!shopContext.cart?.length && !orderComplete) {
      showToast("Jūsu grozs ir tukšs. Pievienojiet preci, lai turpinātu.", "warning");
    }
  }, [shopContext.cart, orderComplete]);

  const handleGoBack = () => {
    navigate(-1);
  };

  // If cart is empty and order is not complete, show empty cart message
  if (!shopContext.cart?.length && !orderComplete) {
    return (
      <div className="pt-20 min-h-screen" style={{ backgroundColor: 'oklch(27.8% 0.033 256.848)' }}>
        <div className="container mx-auto px-4 py-8">
          <div className="bg-gray-900 rounded-lg overflow-hidden shadow-xl p-8">
            <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <h2 className="text-2xl font-bold text-white mb-4">Jūsu grozs ir tukšs</h2>
              <p className="text-gray-300 mb-8">Lūdzu, pievienojiet preci, lai turpinātu.</p>
              <button 
                onClick={() => navigate('/shop')}
                className="bg-green-400 hover:bg-green-500 text-black font-semibold py-3 px-6 rounded-md"
              >
                Doties uz veikalu
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If order is complete, show success message
  if (orderComplete) {
    return (
      <div className="pt-20 min-h-screen" style={{ backgroundColor: 'oklch(27.8% 0.033 256.848)' }}>
        <div className="container mx-auto px-4 py-8">
          <div className="bg-gray-900 rounded-lg overflow-hidden shadow-xl p-8">
            <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
              <div className="bg-green-900 p-8 rounded-lg inline-block mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-3xl font-bold text-white mb-4">Paldies par jūsu pasūtījumu!</h2>
              <p className="text-gray-300 mb-6 text-lg">
                Jūsu pasūtījums ir veiksmīgi pieņemts. Pasūtījuma numurs: <span className="font-bold text-green-400">{orderId}</span>
              </p>
              <p className="text-gray-400 mb-8">
                Apstiprinājums ir nosūtīts uz {formData.shipping.email}. 
                Jūs saņemsiet e-pastu, kad pasūtījums tiks nosūtīts.
              </p>
              <button 
                onClick={() => navigate('/shop')}
                className="bg-green-400 hover:bg-green-500 text-black font-semibold py-3 px-6 rounded-md"
              >
                Turpināt iepirkties
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="pt-20 min-h-screen" style={{ backgroundColor: 'oklch(27.8% 0.033 256.848)' }}>
        {/* Back Button - not shown on order success */}
        {!orderComplete && (
          <div className="container mx-auto px-4 py-4">
            <button 
              onClick={handleGoBack}
              className="flex items-center text-gray-300 hover:text-white"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
              </svg>
              <span>Atpakaļ uz veikalu</span>
            </button>
          </div>
        )}
        
        <div className="container mx-auto px-4 py-8">
          <div className="bg-gray-900 rounded-lg overflow-hidden shadow-xl p-4 md:p-8">
            <h2 className="text-2xl font-bold text-white mb-6 text-center">
              {orderComplete ? 'Pasūtījums pabeigts' : 'Noformēt pasūtījumu'}
            </h2>
            
            {/* Steps Indicator */}
            <div className="mb-8 overflow-x-auto py-2">
              <div className="flex justify-between">
                {['Piegāde', 'Maksājums'].map((step, index) => (
                  <div key={index} className="flex flex-col items-center">
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 font-bold text-sm mb-1 ${
                      currentStep === index 
                        ? 'border-green-400 bg-green-400 text-black' 
                        : currentStep > index 
                          ? 'border-green-400 text-green-400' 
                          : 'border-gray-600 text-gray-600'
                    }`}>
                      {currentStep > index ? '✓' : index + 1}
                    </div>
                    <span className={`text-sm ${
                      currentStep >= index ? 'text-white' : 'text-gray-600'
                    }`}>
                      {step}
                    </span>
                    {index < 1 && (
                      <div className="hidden sm:block w-24 h-0.5 mt-4 mx-2 bg-gray-700"></div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            
            <div className="px-0 md:px-4 py-4">
              {currentStep === 0 && shippingContent}
              {currentStep === 1 && paymentContent}
            </div>
          </div>
        </div>
      </div>

      {/* Toast notification */}
      {toast.visible && (
        <div className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg z-50 ${
          toast.type === 'error' ? 'bg-red-500' :
          toast.type === 'success' ? 'bg-green-500' :
          toast.type === 'warning' ? 'bg-yellow-500' :
          'bg-blue-500'
        }`}>
          {toast.message}
        </div>
      )}
    </>
  );
};

export default React.memo(Checkout);